// User account endpoints: profile, referral, daily-omen, daily-checkin,
// inbox, avatar. (Friends/wall live in social.ts — they're peer-facing.)

import { ApiError, Auth, call, handleUnauthorized } from './core';
import type {
  CheckinToday,
  DailyCheckin,
  DailyOmenRecord,
  FortuneType,
  InboxPage,
  MyReferral,
  OmenToday,
  RedeemReferralResult,
  ReferralSource,
  UserMe,
} from './types';

export const userApi = {
  // Profile
  me: () => call<UserMe>('/users/me'),
  updateProfile: (payload: { nickname?: string; onboarding_complete?: boolean }) =>
    call<UserMe>('/users/me/profile', {
      method: 'PUT',
      body: JSON.stringify(payload),
    }),

  // Compliance Baseline (STA-128, STA-133) — POST after AuthCallback.
  submitConsent: (consent: {
    is_adult: boolean;
    tos_agreed: boolean;
    privacy_policy_agreed: boolean;
  }) =>
    call('/users/me/consent', {
      method: 'POST',
      body: JSON.stringify(consent),
    }),

  // Referral (RC-P1 / RC-P2)
  myReferral: () => call<MyReferral>('/me/referral'),
  redeemReferral: (code: string, source: ReferralSource) =>
    call<RedeemReferralResult>('/me/referral/redeem', {
      method: 'POST',
      body: JSON.stringify({ code, source }),
    }),

  // Daily check-in (RC-P3) — legacy, kept for native-app contract.
  checkinToday: () => call<CheckinToday>('/daily-checkin/today'),
  doCheckin: (fortune_type: FortuneType) =>
    call<DailyCheckin>('/daily-checkin', {
      method: 'POST',
      body: JSON.stringify({ fortune_type }),
    }),

  // STA-148 — Daily Omen (web's daily engagement loop).
  omenToday: () => call<OmenToday>('/daily-omen/today'),
  drawOmen: (note: string) =>
    call<DailyOmenRecord>('/daily-omen', {
      method: 'POST',
      body: JSON.stringify({ note }),
    }),
  omenHistory: (days = 30) =>
    call<DailyOmenRecord[]>(`/daily-omen/history?days=${days}`),

  // Mailbox (RC-P5)
  listMessages: (params: { unread_only?: boolean; limit?: number; cursor?: string | null } = {}) => {
    const q = new URLSearchParams();
    if (params.unread_only) q.set('unread_only', 'true');
    if (params.limit) q.set('limit', String(params.limit));
    if (params.cursor) q.set('cursor', params.cursor);
    const qs = q.toString();
    return call<InboxPage>(`/me/messages${qs ? `?${qs}` : ''}`);
  },
  unreadMessageCount: () =>
    call<{ unread_count: number }>('/me/messages/unread-count'),
  markMessageRead: (id: string) =>
    call<void>(`/me/messages/${id}/read`, { method: 'POST' }),

  // STA-153 — avatar upload (multipart/form-data) + revert.
  async uploadAvatar(file: File): Promise<UserMe> {
    const fd = new FormData();
    fd.append('file', file);
    const t = Auth.getToken();
    const res = await fetch('/api/v1/users/me/avatar', {
      method: 'POST',
      headers: t ? { Authorization: `Bearer ${t}` } : {},
      body: fd,
    });
    handleUnauthorized(res.status, /* authed */ true);
    if (!res.ok) {
      let detail: unknown;
      try {
        detail = await res.json();
      } catch {
        detail = await res.text();
      }
      throw new ApiError(res.status, detail, `${res.status} ${res.statusText}`);
    }
    return (await res.json()) as UserMe;
  },
  deleteAvatar: () =>
    call<UserMe>('/users/me/avatar', { method: 'DELETE' }),
};
